import { Observable } from 'rxjs';
import { I18n as I } from 'react-redux-i18n';

import {
  getDeepProp,
  ifNullOrWhitespace,
  isArray,
  isFunc,
  isNil,
  isNullOrWhitespace,
  isString,
} from 'vdlib';
import { AlertTypes, ResponseErrorTypes } from 'vdlib';
import { APP_ACTION_TYPES as AT, showAlert } from 'data/app/App.actions';

export const defaultEpicOptions = {
  errorMessage: null, // string or function
  suppressErrorAlert: false,
  successMessage: null,
};

const translatableLookups = [
  'countries',
  'regions',
  'imitationPartCategories',
  'usedConditionTypes',
];

export const setFilterFromAction = (action, stateFilters = []) => {
  if (!action || !action.payload) return stateFilters;

  const newFilter = {
    Name: action.payload.name,
    Value: action.payload.value,
  };

  const existing = !!stateFilters.find(f => f.Name === newFilter.Name);
  const isEmpty =
    !Array.isArray(newFilter.Value) &&
    typeof newFilter.Value !== 'number' &&
    isNullOrWhitespace(newFilter.Value);

  const newFilters = isEmpty
    ? stateFilters.filter(f => f.Name !== newFilter.Name)
    : existing
    ? stateFilters.map(f => (f.Name === newFilter.Name ? newFilter : f))
    : [...stateFilters, newFilter];

  return newFilters;
};

const ReduxHelper = {
  createAction: (type, payload, error) => {
    let action = { type };
    if (!isNil(payload)) action.payload = payload;
    if (!isNil(error)) action.error = error;
    return action;
  },
  extractError: action =>
    action.error && action.error.message ? action.error.message : '',
  extractResponseError: (exception, presetErrorMessage) => {
    if (isNil(exception)) exception = {};

    const hasResponse = !!exception && !!exception.response,
      responseData = hasResponse ? exception.response.data : {},
      statusCode =
        exception.response && exception.response.status
          ? Math.floor(exception.response.status / 100)
          : 0;

    return {
      type:
        statusCode === 0
          ? ResponseErrorTypes.UserInterface
          : statusCode === 5
          ? ResponseErrorTypes.System
          : exception.response.status === 401
          ? ResponseErrorTypes.Unauthenticated
          : statusCode === 4
          ? ResponseErrorTypes.Unauthorized
          : statusCode === 2
          ? ResponseErrorTypes.Validation
          : ResponseErrorTypes.Other,
      message: presetErrorMessage || responseData.Message || exception.message,
      stack: exception.stack,
    };
  },

  createActionTypeTriplet: (actionName, withClear) => {
    var obj = {
      [`${actionName}`]: `${actionName}`,
      [`${actionName}_SUCCESS`]: `${actionName}_SUCCESS`,
      [`${actionName}_FAIL`]: `${actionName}_FAIL`,
    };

    if (withClear === true) {
      let clearActionName =
        actionName.indexOf('_FETCH') >= 0
          ? actionName.replace('_FETCH', '_CLEAR')
          : actionName + '_CLEAR';
      obj[clearActionName] = clearActionName;
    }

    return obj;
  },

  createDefaultEpics: (...defaultEpicsArr) => {
    if (!isArray(defaultEpicsArr)) return null;

    return defaultEpicsArr
      .map(ReduxHelper.createDefaultEpic)
      .filter(epic => isFunc(epic));
  },

  createDefaultEpic: epicData => {
    if (!isArray(epicData)) return;

    let [initialActionName, apiMethod] = epicData,
      opts = Object.assign({}, defaultEpicOptions, epicData[2]);

    if (isNullOrWhitespace(initialActionName) || !isFunc(apiMethod)) return;

    let config = {
      errorCallback: e => {
        let error = ReduxHelper.extractResponseError(e, opts.errorMessage);
        if (error.type === ResponseErrorTypes.Unauthenticated)
          return Observable.from([
            ReduxHelper.createAction(`${initialActionName}_FAIL`, null, e),
            ReduxHelper.createAction(AT.LOGOUT),
          ]);

        return opts.suppressErrorAlert
          ? Observable.of(
              ReduxHelper.createAction(`${initialActionName}_FAIL`, null, e)
            )
          : !isNil(opts.errorMessage)
          ? Observable.from([
              ReduxHelper.createAction(`${initialActionName}_FAIL`, null, e),
              showAlert(
                isFunc(opts.errorMessage)
                  ? opts.errorMessage(e)
                  : ifNullOrWhitespace(
                      opts.errorMessage,
                      ReduxHelper.extractResponseError(e)
                    ),
                AlertTypes.Error
              ),
            ])
          : !isNil(error)
          ? Observable.from([
              ReduxHelper.createAction(`${initialActionName}_FAIL`, null, e),
              showAlert(error.message.Description, AlertTypes.Error),
            ])
          : Observable.of(
              ReduxHelper.createAction(`${initialActionName}_FAIL`, null, e)
            );
      },
      successCallback: isNil(opts.successMessage)
        ? content => [
            ReduxHelper.createAction(`${initialActionName}_SUCCESS`, content),
          ]
        : content => [
            showAlert(
              isFunc(opts.successMessage)
                ? opts.successMessage(content)
                : opts.successMessage,
              AlertTypes.Success
            ),
            ReduxHelper.createAction(`${initialActionName}_SUCCESS`, content),
          ],
    };

    return (action$, store) =>
      action$
        .ofType(initialActionName)
        .mergeMap(action =>
          apiMethod
            .apply(null, [action.payload])
            .flatMap(config.successCallback)
            .catch(config.errorCallback)
        );
  },

  formatMessage: (textOrAction, type) => ({
    text: isString(textOrAction)
      ? textOrAction
      : (textOrAction || {}).error || (textOrAction || {}).payload,
    type: type || AlertTypes.Error,
  }),

  mapStateWithLookups: (props, state, ...lookupNames) => {
    if (!lookupNames) return props;

    let lookupsObj = {},
      lookupsState = state.app.lookups || {};
    for (let lookupName of lookupNames) {
      let sta = lookupName.substr(1),
        key = lookupName.substr(0, 1).toLowerCase() + sta,
        lookup = lookupsState[key];
      if (!lookup) continue;

      lookupsObj[key] =
        translatableLookups.indexOf(key) >= 0
          ? lookupsState[key].map(f =>
              Object.assign({}, f, {
                ID: f.ID,
                Description: I.t(f.Description),
              })
            )
          : lookupsState[key];
    }

    return Object.assign({}, lookupsObj, props);
  },

  extract: (stateObj, ...keys) =>
    keys.reduce((obj, key) => {
      let [parts, targetName] = key.split('->');
      let [type, name] = parts.split('#');
      if (!name) name = type;
      if (!targetName) targetName = name;
      const targetParts = targetName.split('.');
      targetName = targetParts[targetParts.length - 1];

      if (type === 'remote') {
        obj[targetName] = getDeepProp(stateObj, name);
        obj[`${targetName}Error`] = getDeepProp(stateObj, `${name}Error`);
        obj[`${targetName}Pending`] = getDeepProp(stateObj, `${name}Pending`);
      } else {
        obj[targetName] = getDeepProp(stateObj, type);
      }

      return obj;
    }, {}),
};

export const getStateExtractor =
  (...keys) =>
  state =>
    ReduxHelper.extract(state, ...keys);

export default ReduxHelper;
